import React, { useState } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaTimes } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    company: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNotification({ show: false, type: '', message: '' });

    try {
      const response = await fetch('https://backend.tdconsulting.co.tz/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      await response.json();
      setNotification({
        show: true,
        type: 'success',
        message: 'Thank you for your message! We will get back to you soon.'
      });
      setFormData({ name: '', email: '', subject: '', company: '', message: '' });

      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setNotification({
        show: true,
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again later.'
      });

      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, type: '', message: '' });
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Header Section */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-primary-900 text-white py-20 lg:py-28 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl"></div>

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div className="inline-block mb-4" variants={fadeInUp}>
            <span className="bg-secondary-500 text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
              Get In Touch
            </span>
          </motion.div>
          <motion.h1 className="text-4xl lg:text-6xl font-bold mb-6" variants={fadeInUp}>
            Contact <span className="text-secondary-500">Us</span>
          </motion.h1>
          <motion.p className="text-lg lg:text-xl leading-relaxed max-w-4xl mx-auto opacity-90" variants={fadeInUp}>
            Ready to work together? Get in touch with us today.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Content */}
      <section className="py-16 lg:py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-6">
              <div className="text-center lg:text-left mb-8">
                <h2 className="text-3xl font-bold text-navy-900 dark:text-white mb-3">Contact Information</h2>
                <div className="w-24 h-1 bg-secondary-500 mx-auto lg:mx-0 rounded-full"></div>
              </div>

          

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border-2 border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-navy-900 rounded-xl shadow-lg mr-4 flex-shrink-0">
                    <FaEnvelope className="text-xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2 text-navy-900 dark:text-white">General Enquiries</h3>
                    <a href="mailto:info@tdconsulting.co.tz" className="text-secondary-500 hover:text-secondary-600 transition-colors text-sm">
                      info@tdconsulting.co.tz
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border-2 border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-navy-900 rounded-xl shadow-lg mr-4 flex-shrink-0">
                    <FaEnvelope className="text-xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2 text-navy-900 dark:text-white">Mailing Address</h3>
                    <p className="text-gray-600 dark:text-gray-300">P.O. BOX 229<br />ZANZIBAR, TANZANIA</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border-2 border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-navy-900 rounded-xl shadow-lg mr-4 flex-shrink-0">
                    <FaMapMarkerAlt className="text-xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-3 text-navy-900 dark:text-white">Office Locations</h3>
                    <div className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
                      <p><strong className="text-gray-800 dark:text-gray-200">Zanzibar:</strong> Stone Town, Zanzibar</p>
                      <p><strong className="text-gray-800 dark:text-gray-200">Dar es Salaam:</strong> Commercial District</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border-2 border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-navy-900 rounded-xl shadow-lg mr-4 flex-shrink-0">
                    <FaClock className="text-xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-3 text-navy-900 dark:text-white">Business Hours</h3>
                    <div className="space-y-1 text-gray-600 dark:text-gray-300 text-sm">
                      <p>Monday - Friday: 8:00 AM - 5:00 PM (EAT)</p>
                      <p>Saturday: 9:00 AM - 1:00 PM (EAT)</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="text-center lg:text-left mb-8">
                <h2 className="text-3xl font-bold text-navy-900 dark:text-white mb-3">Send Us a Message</h2>
                <div className="w-24 h-1 bg-secondary-500 mx-auto lg:mx-0 rounded-full"></div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg border-2 border-gray-100 dark:border-gray-700">
                {/* Notification Message */}
                {notification.show && (
                  <div className={`p-4 rounded-xl flex items-start justify-between animate-slide-in ${
                    notification.type === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-500'
                      : 'bg-red-50 dark:bg-red-900/20 border-2 border-red-500'
                  }`}>
                    <div className="flex items-start">
                      {notification.type === 'success' ? (
                        <FaCheckCircle className="text-green-500 text-xl mt-0.5 mr-3 flex-shrink-0" />
                      ) : (
                        <FaExclamationCircle className="text-red-500 text-xl mt-0.5 mr-3 flex-shrink-0" />
                      )}
                      <p className={`font-medium ${
                        notification.type === 'success'
                          ? 'text-green-800 dark:text-green-200'
                          : 'text-red-800 dark:text-red-200'
                      }`}>
                        {notification.message}
                      </p>
                    </div>
                    <button
                      onClick={() => setNotification({ show: false, type: '', message: '' })}
                      className={`ml-4 flex-shrink-0 ${
                        notification.type === 'success'
                          ? 'text-green-500 hover:text-green-700'
                          : 'text-red-500 hover:text-red-700'
                      } transition-colors`}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-navy-900 dark:text-white mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-navy-900 dark:focus:border-secondary-500 focus:outline-none transition-colors bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-navy-900 dark:text-white mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-navy-900 dark:focus:border-secondary-500 focus:outline-none transition-colors bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-bold text-navy-900 dark:text-white mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-navy-900 dark:focus:border-secondary-500 focus:outline-none transition-colors bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="What is this regarding?"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-bold text-navy-900 dark:text-white mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-navy-900 dark:focus:border-secondary-500 focus:outline-none transition-colors bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-navy-900 dark:text-white mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project or how we can help you..."
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:border-navy-900 dark:focus:border-secondary-500 focus:outline-none transition-colors bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-vertical"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-navy-900 text-white py-4 px-6 rounded-xl font-semibold hover:bg-navy-800 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <FaPaperPlane className="mr-2" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Consult Us Today.
          </h2>
          <p className="text-lg lg:text-xl leading-relaxed mb-10 text-gray-200">
            Whether you're a government ministry seeking a strategic plan, a non-profit measuring impact,
            or an investor exploring opportunities in Tanzania, we are here to guide you every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/services"
              className="inline-flex items-center justify-center px-10 py-4 bg-secondary-500 text-white rounded-full font-semibold text-lg hover:bg-secondary-600 hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Explore Our Services
              <span className="ml-2">→</span>
            </a>
            <a
              href="/about"
              className="inline-flex items-center justify-center px-10 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold text-lg hover:bg-white hover:text-navy-900 transition-all duration-300"
            >
              Learn More About Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;